import { Typography } from '@mui/material'
import React from 'react'
import * as datefns from 'date-fns'
import { sv } from 'date-fns/locale'

/**
 * False if v is null, undefined, or an empty string, true otherwise.
 * @param {*} v value to check
 */
function hasValue(v) {
    return v !== null && v !== undefined && v !== ''
}

function checkMandatoryFields(issue, fields, missingFields) {
    // if fields is not a list, make it a list
    if (!Array.isArray(fields)) {
        fields = [fields]
    }
    fields.forEach((field) => {
        if (!hasValue(issue[field])) {
            missingFields.push(field)
        }
    })
}

function checkAtLeastOneField(issue, fields, missingFields) {
    const booleanFields = ['unlisted', 'isVwap', 'isVwapDays', 'priceMinQuota', 'noPriceMax']
    const found = fields.some((field) => {
        if (booleanFields.includes(field)) {
            return !!issue[field]
        } else {
            return hasValue(issue[field])
        }
    })
    if (!found) {
        missingFields.push(...fields)
    }
}

const earliestDate = (listOfDates) => {
    if (!listOfDates || listOfDates.length === 0) {
        return null
    }
    if (listOfDates.length === 1) {
        return new Date(listOfDates[0])
    }
    return listOfDates.reduce((a, b) => {
        if (!a) return new Date(b)
        if (!b) return new Date(a)
        const aDate = new Date(a)
        const bDate = new Date(b)
        return datefns.isBefore(aDate, bDate) ? aDate : bDate
    })
}

const latestDate = (listOfDates) => {
    if (!listOfDates || listOfDates.length === 0) {
        return null
    }
    if (listOfDates.length === 1) {
        return new Date(listOfDates[0])
    }
    return listOfDates.reduce((a, b) => {
        if (!a) return new Date(b)
        if (!b) return new Date(a)
        const aDate = new Date(a)
        const bDate = new Date(b)
        return datefns.isAfter(aDate, bDate) ? aDate : bDate
    })
}

function calculateWarrantStatus(warrant) {
    if (!warrant) return null
    if (warrant.cancelled) {
        return { stage: 5, status: 'complete', missingFields: [] }
    }
    let statusStage = 1 // created
    if (warrant.subscriptionPeriods?.length > 0) {
        const today = new Date()
        const completedPeriods = warrant.subscriptionPeriods.filter((p) =>
            datefns.isBefore(new Date(p.subEndDate), today),
        )
        const ongoingPeriods = warrant.subscriptionPeriods.filter(
            (p) => datefns.isBefore(new Date(p.subStartDate), today) && datefns.isAfter(new Date(p.subEndDate), today),
        )
        const futurePeriods = warrant.subscriptionPeriods.filter((p) =>
            datefns.isAfter(new Date(p.subStartDate), today),
        )

        if (ongoingPeriods.length > 0) {
            statusStage = 2
        } else if (completedPeriods.length > 0 && futurePeriods.length === 0) {
            statusStage = 3
        }
    }
    let missingFields = []
    if (statusStage >= 1) {
        if (warrant.instrumentId === null) {
            // Name is only mandatory if there is not an instrument connected to the warrant
            checkMandatoryFields(warrant, 'name', missingFields)
        }
        if (warrant.origins?.length > 0) {
            warrant.origins.forEach((origin, index) => {
                const originMissingFields = []
                if (origin.type === 'Rights issue') {
                    checkMandatoryFields(origin, 'warrantsPerUnit', originMissingFields)
                }
                if (origin.statusStage >= 4) {
                    checkMandatoryFields(origin, 'numberOfWarrants', originMissingFields)
                }
                if (originMissingFields.length > 0) {
                    missingFields.push(`origins[${index + 1}]: [${originMissingFields.join(', ')}]`)
                }
            })
        } else {
            missingFields.push('origins')
        }
        if (!warrant.subscriptionPeriods?.length > 0) {
            missingFields.push('subscriptionPeriods')
        } else {
            warrant.subscriptionPeriods.forEach((period, index) => {
                const periodMissingFields = []
                checkMandatoryFields(
                    period,
                    [`subStartDate`, 'subEndDate', 'warrantsRequired', 'sharesReceived'],
                    periodMissingFields,
                )
                if (period.isVwap) {
                    checkMandatoryFields(period, ['vwapDiscount', 'vwapDays', 'vwapPeriodEnd'], periodMissingFields)
                    checkAtLeastOneField(period, ['priceMinQuota', 'priceMin'], periodMissingFields)
                    checkAtLeastOneField(period, ['noPriceMax', 'priceMax'], periodMissingFields)
                } else {
                    checkMandatoryFields(period, 'warrantSubPrice', periodMissingFields)
                }

                if (periodMissingFields.length > 0) {
                    missingFields.push(`subscriptionPeriods[${index + 1}]: [${periodMissingFields.join(', ')}]`)
                }
            })
        }
    }
    if (statusStage >= 2) {
        if (warrant.subscriptionPeriods?.length > 0) {
            warrant.subscriptionPeriods.forEach((period, index) => {
                const periodMissingFields = []
                checkMandatoryFields(period, 'warrantSubPrice', periodMissingFields)
                if (periodMissingFields.length > 0) {
                    missingFields.push(`subscriptionPeriods[${index + 1}]: [${periodMissingFields.join(', ')}]`)
                }
            })
        }
    }
    if (statusStage >= 3) {
        if (warrant.subscriptionPeriods?.length > 0) {
            warrant.subscriptionPeriods.forEach((period, index) => {
                const periodMissingFields = []
                checkMandatoryFields(period, 'sharesSubscribed', periodMissingFields)
                if (periodMissingFields.length > 0) {
                    missingFields.push(`subscriptionPeriods[${index + 1}]: [${periodMissingFields.join(', ')}]`)
                }
            })
        }
    }
    // removed duplicate fields
    if (missingFields.length > 0) {
        missingFields = [...new Set(missingFields)]
        return { stage: statusStage, status: 'incomplete', missingFields }
    }
    if (statusStage === 3) {
        statusStage = 9 // this is the "all done" status
    }
    return { stage: statusStage, status: 'complete', missingFields: [] }
}

function WarrantStatusDescription({ warrantStatus, warrant, shortForm = false }) {
    // const { t } = useTranslation()
    warrantStatus = warrantStatus || calculateWarrantStatus(warrant)
    if (!warrantStatus) {
        return null
    }
    let { stage, status, missingFields } = warrantStatus
    if (Array.isArray(missingFields)) {
        missingFields = missingFields.join(', ')
    }
    const warrantStatusLabels = {
        1: 'Utgiven',
        2: 'Pågående',
        3: 'Avslutad',
        5: 'Avbruten',
        9: 'Klar',
    }
    const stageLabel = warrantStatusLabels[stage] || `Annat: ${stage}`
    const statusLabel = status === 'complete' ? 'klar' : 'inte klar'
    const sx = { color: status === 'complete' ? 'green' : 'red' }
    if (!warrantStatus.stage) {
        return null
    }
    if (warrantStatus.stage === 9 && warrantStatus.status === 'complete') {
        // this is the "all done" status, no need to show anything
        return (
            <Typography variant='body1' gutterBottom display='block' sx={sx}>
                Klar
            </Typography>
        )
    }
    if (warrantStatus.stage === 5) {
        // this is the "all done" status, no need to show anything
        return (
            <Typography variant='body1' gutterBottom display='block' sx={sx}>
                Avbruten
            </Typography>
        )
    }
    const hideUntil = warrant?.statusHideUntil || warrantStatus.hideUntil
    if (hideUntil && new Date(hideUntil) > new Date()) {
        return (
            <Typography variant='body1' gutterBottom display='block' sx={{ color: 'orange' }}>
                Avvakta till {datefns.format(new Date(hideUntil), 'MMM d', { locale: sv })}
            </Typography>
        )
    }

    if (shortForm) {
        return (
            <Typography variant='body1' gutterBottom display='block' sx={sx}>
                {stageLabel}, {statusLabel}.
            </Typography>
        )
    }
    return (
        <Typography variant='body1' gutterBottom display='block' sx={sx}>
            {stageLabel}, {statusLabel}.
            {missingFields && <React.Fragment> Saknade fält: {missingFields?.split(',')?.join(', ')}</React.Fragment>}
        </Typography>
    )
}

export { WarrantStatusDescription, calculateWarrantStatus }
